import { useEffect, useState, type ReactNode } from "react";
import { INLINE_LABELS } from "../labels.ts";

// Loading placeholders (docs/design-system.md §0.3c): the layout of what is
// about to appear, in one neutral tone, so nothing jumps when the data comes.
// Only what waits for data is drawn; the shell, breadcrumbs and tabs render as
// usual. Nothing is drawn for the first 200 ms, while a screen reader hears
// "Loading…" from the start.

/** The basic shapes, one per kind of content. */
export type SkeletonPart = "list" | "groups" | "header" | "text" | "props" | "stats" | "form";
/** A basic shape, or one page form: a list page, an object page, a detail panel, or the shell itself. */
export type SkeletonShape = SkeletonPart | "page" | "object" | "panel" | "frame";

export const SKELETON_DELAY_MS = 200;

// Uneven widths so placeholder rows read as rows, not as a block.
const TITLE_WIDTHS = ["46%", "62%", "38%", "54%", "70%"];
const TEXT_WIDTHS = ["100%", "94%", "97%", "88%"];
const VALUE_WIDTHS = ["44%", "60%", "36%", "52%", "40%"];
const MAX_ROWS = 5;

const Bar = ({ width, className }: { width?: string; className?: string }) =>
  <span className={className ? `z-skel-bar ${className}` : "z-skel-bar"} style={width ? { width } : undefined} />;

const times = (count: number) => Array.from({ length: Math.min(Math.max(1, count), MAX_ROWS) }, (_, index) => index);

function Rows({ rows }: { rows: number }) {
  return <>{times(rows).map((i) => <div key={i} className="z-skel-row">
    <Bar className="z-skel-dot" />
    <span className="z-skel-grow"><Bar width={TITLE_WIDTHS[i % TITLE_WIDTHS.length]} /></span>
    <Bar className="z-skel-pill z-skel-chip" width="56px" />
    <Bar width="44px" />
  </div>)}</>;
}

/** One basic shape; `rows` overrides its usual count. */
function Part({ part, rows }: { part: SkeletonPart; rows?: number }) {
  switch (part) {
    case "list": return <div className="z-skel-box"><Rows rows={rows ?? 3} /></div>;
    case "groups": return <div className="z-skel-box"><div className="z-skel-group"><Bar width="96px" /></div><Rows rows={rows ?? 5} /></div>;
    case "header": return <div className="z-skel-header">
      <Bar className="z-skel-title" width="240px" />
      <div className="z-skel-chips"><Bar className="z-skel-pill" width="72px" /><Bar className="z-skel-pill" width="96px" /><Bar className="z-skel-pill" width="64px" /></div>
    </div>;
    case "text": {
      const lines = times(rows ?? 3);
      return <div className="z-skel-text">{lines.map((i) => <Bar key={i} width={i === lines.length - 1 && i > 0 ? "60%" : TEXT_WIDTHS[i % TEXT_WIDTHS.length]} />)}</div>;
    }
    case "props": return <div className="z-skel-props">{times(rows ?? 4).map((i) => [<Bar key={`l${i}`} width="72px" />, <Bar key={`v${i}`} width={VALUE_WIDTHS[i % VALUE_WIDTHS.length]} />])}</div>;
    case "stats": return <div className="z-skel-stats">{times(rows ?? 3).map((i) => <div key={i} className="z-skel-stat"><Bar width="40%" /><Bar className="z-skel-figure" width="48px" /><Bar width="64%" /></div>)}</div>;
    case "form": return <div className="z-skel-form">{times(rows ?? 3).map((i) => <div key={i} className="z-skel-field"><Bar width="96px" /><Bar className="z-skel-input" /></div>)}</div>;
  }
}

function Shapes({ shape, rows }: { shape: Exclude<SkeletonShape, "frame"> | SkeletonPart[]; rows?: number }): ReactNode {
  if (Array.isArray(shape)) return shape.map((part, i) => <Part key={`${part}-${i}`} part={part} rows={rows} />);
  switch (shape) {
    case "page": return <><Part part="header" /><Part part="groups" rows={rows} /></>;
    case "object": return <><Part part="header" /><div className="z-skel-columns">
      <div><Part part="text" /><Part part="list" rows={rows} /></div>
      <div><Part part="props" /></div>
    </div></>;
    case "panel": return <><Part part="header" /><Part part="props" rows={rows} /><Part part="text" /></>;
    default: return <Part part={shape} rows={rows} />;
  }
}

/** The shell before the session is known: the sidebar's rows beside an inset panel holding a list page. */
function Frame({ shown }: { shown: boolean }) {
  const nav = (key: string, width: string, kind?: "is-top" | "is-label" | "is-sub") =>
    <div key={key} className={kind ? `z-skel-nav ${kind}` : "z-skel-nav"}>{kind !== "is-label" && <Bar className="z-skel-icon" />}<Bar width={width} /></div>;
  return <>
    <div className="z-side" aria-hidden>{shown && <div className="z-skel-body z-skel-side">
      {nav("workspace", "112px", "is-top")}{nav("inbox", "64px")}{nav("playbooks", "80px")}
      {nav("products", "56px", "is-label")}{nav("product", "72px")}
      {["48px", "72px", "40px", "40px", "64px"].map((width, i) => nav(`page-${i}`, width, "is-sub"))}
    </div>}</div>
    <div className="z-main" aria-hidden>
      <div className="z-topbar"><div className="z-pagebar" /></div>
      <div className="z-page wide">{shown && <div className="z-skel-body"><Shapes shape="page" /></div>}</div>
    </div>
  </>;
}

function useShown(delay: number) {
  const [shown, setShown] = useState(delay <= 0);
  useEffect(() => {
    if (delay <= 0) return;
    const timer = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  return shown;
}

/**
 * Placeholders in the shape of what is loading. Several basic shapes stack in
 * the order given; `rows` sets the count of the list, text or property rows.
 */
export function Skeleton({ shape = "page", rows, label = INLINE_LABELS.loading, delay = SKELETON_DELAY_MS }: {
  shape?: SkeletonShape | SkeletonPart[]; rows?: number; label?: string; delay?: number;
}) {
  const shown = useShown(delay);
  if (shape === "frame") return <div className="z-app" role="status" aria-busy="true"><span className="z-sr">{label}</span><Frame shown={shown} /></div>;
  return <div className="z-skel" role="status" aria-busy="true">
    <span className="z-sr">{label}</span>
    {shown && <div className="z-skel-body" aria-hidden><Shapes shape={shape} rows={rows} /></div>}
  </div>;
}
